const expressCheatsheet = {
  id: "express",
  title: "Express.js Cheatsheet",
  description:
    "A comprehensive guide to Express.js including routing, middleware, error handling, and best practices",
  language: "javascript",
  category: "Programming & Backend",
  level: "All Levels",
  sections: [
    {
      title: "Getting Started",
      items: [
        {
          title: "Basic Setup",
          content: "Initialize an Express application",
          code: `// Install Express
npm install express

// Basic Express App
const express = require('express');
const app = express();

// Basic middleware
app.use(express.json());
app.use(express.urlencoded({ extended: true }));

// Start server
const PORT = process.env.PORT || 3000;
app.listen(PORT, () => {
  console.log(\`Server is running on port \${PORT}\`);
});`,
          note: "Express.js is a minimal and flexible Node.js web application framework",
        },
        {
          title: "Express Generator",
          content: "Quickly create an Express application skeleton",
          code: `// Install Express Generator
npm install -g express-generator

// Create new Express app
express myapp --view=ejs
cd myapp
npm install

// Start the app
npm start`,
          note: "Express Generator helps create an application skeleton with common middleware and structure",
        },
      ],
    },
    {
      title: "Routing",
      items: [
        {
          title: "Basic Routing",
          content: "Handle different HTTP methods and routes",
          code: `// Basic routes
app.get('/', (req, res) => {
  res.send('Hello World');
});

app.post('/api/users', (req, res) => {
  res.json({ message: 'User created' });
});

// Route parameters
app.get('/users/:id', (req, res) => {
  const userId = req.params.id;
  res.send(\`User ID: \${userId}\`);
});

// Query strings
app.get('/search', (req, res) => {
  const query = req.query.q;
  res.send(\`Search query: \${query}\`);
});

// Multiple route handlers
app.route('/book')
  .get((req, res) => {
    res.send('Get a book');
  })
  .post((req, res) => {
    res.send('Add a book');
  })
  .put((req, res) => {
    res.send('Update a book');
  });`,
          note: "Express provides flexible routing to handle HTTP requests",
        },
        {
          title: "Router Module",
          content: "Modular route handling using Express Router",
          code: `// users.js - Router module
const express = require('express');
const router = express.Router();

router.get('/', (req, res) => {
  res.send('Users list');
});

router.post('/', (req, res) => {
  res.send('Create user');
});

module.exports = router;

// app.js - Main application
const usersRouter = require('./routes/users');
app.use('/users', usersRouter);`,
          note: "Router modules help organize routes in separate files",
        },
      ],
    },
    {
      title: "Middleware",
      items: [
        {
          title: "Custom Middleware",
          content: "Create and use custom middleware functions",
          code: `// Application-level middleware
app.use((req, res, next) => {
  console.log(\`\${req.method} \${req.url}\`);
  next();
});

// Route-specific middleware
const authenticate = (req, res, next) => {
  const authHeader = req.headers.authorization;
  if (!authHeader) {
    return res.status(401).json({ error: 'No token provided' });
  }
  next();
};

app.get('/protected', authenticate, (req, res) => {
  res.send('Protected route');
});

// Error handling middleware
app.use((err, req, res, next) => {
  console.error(err.stack);
  res.status(500).send('Something broke!');
});`,
          note: "Middleware functions have access to request, response objects and the next middleware function",
        },
        {
          title: "Common Middleware",
          content: "Essential third-party middleware",
          code: `// Body parser
app.use(express.json());
app.use(express.urlencoded({ extended: true }));

// Static files
app.use(express.static('public'));

// Cookie parser
const cookieParser = require('cookie-parser');
app.use(cookieParser());

// Session handling
const session = require('express-session');
app.use(session({
  secret: 'your secret key',
  resave: false,
  saveUninitialized: true,
  cookie: { secure: true }
}));

// CORS
const cors = require('cors');
app.use(cors());

// Compression
const compression = require('compression');
app.use(compression());`,
          note: "These middleware packages are commonly used in Express applications",
        },
      ],
    },
    {
      title: "Request & Response",
      items: [
        {
          title: "Request Object",
          content: "Common request object properties and methods",
          code: `app.get('/example', (req, res) => {
  // URL parameters
  const params = req.params;
  
  // Query string
  const query = req.query;
  
  // Request body
  const body = req.body;
  
  // Headers
  const headers = req.headers;
  
  // Cookies
  const cookies = req.cookies;
  
  // Session
  const session = req.session;
  
  // File uploads
  const files = req.files;
  
  // Request method
  const method = req.method;
  
  // Request URL
  const url = req.url;
});`,
          note: "The request object contains all the information about the incoming HTTP request",
        },
        {
          title: "Response Object",
          content: "Common response methods and properties",
          code: `app.get('/api/examples', (req, res) => {
  // Send JSON response
  res.json({ message: 'Success' });
  
  // Send plain text
  res.send('Plain text response');
  
  // Send file
  res.sendFile('/path/to/file.pdf');
  
  // Render template
  res.render('index', { title: 'Home' });
  
  // Redirect
  res.redirect('/another-page');
  
  // Set status
  res.status(201).send('Created');
  
  // Set headers
  res.set('Content-Type', 'application/json');
  
  // Send status
  res.sendStatus(404);
  
  // Chain methods
  res.status(200)
     .set('Content-Type', 'application/json')
     .json({ success: true });
});`,
          note: "The response object provides methods to send responses back to the client",
        },
      ],
    },
    {
      title: "Error Handling",
      items: [
        {
          title: "Error Middleware",
          content: "Handle errors in Express applications",
          code: `// Custom error class
class AppError extends Error {
  constructor(message, statusCode) {
    super(message);
    this.statusCode = statusCode;
    this.status = \`\${statusCode}\`.startsWith('4') ? 'fail' : 'error';
    Error.captureStackTrace(this, this.constructor);
  }
}

// Error handling middleware
app.use((err, req, res, next) => {
  err.statusCode = err.statusCode || 500;
  err.status = err.status || 'error';

  if (process.env.NODE_ENV === 'development') {
    res.status(err.statusCode).json({
      status: err.status,
      error: err,
      message: err.message,
      stack: err.stack
    });
  } else {
    res.status(err.statusCode).json({
      status: err.status,
      message: err.message
    });
  }
});

// Async error handling
const asyncHandler = fn => (req, res, next) =>
  Promise.resolve(fn(req, res, next)).catch(next);

// Usage
app.get('/async-route', asyncHandler(async (req, res) => {
  const data = await someAsyncOperation();
  res.json(data);
}));`,
          note: "Proper error handling is crucial for building robust Express applications",
        },
      ],
    },
    {
      title: "Security Best Practices",
      items: [
        {
          title: "Security Middleware",
          content: "Essential security middleware and configurations",
          code: `// Security headers with helmet
const helmet = require('helmet');
app.use(helmet());

// Rate limiting
const rateLimit = require('express-rate-limit');
const limiter = rateLimit({
  windowMs: 15 * 60 * 1000, // 15 minutes
  max: 100 // limit each IP to 100 requests per windowMs
});
app.use('/api', limiter);

// XSS protection
app.use(helmet.xssFilter());

// CSRF protection
const csrf = require('csurf');
app.use(csrf({ cookie: true }));

// SQL injection prevention
const { body, validationResult } = require('express-validator');
app.post('/user',
  body('email').isEmail().normalizeEmail(),
  body('password').isLength({ min: 6 }),
  (req, res) => {
    const errors = validationResult(req);
    if (!errors.isEmpty()) {
      return res.status(400).json({ errors: errors.array() });
    }
    // Process request
  }
);`,
          note: "Implementing security measures is essential for protecting your Express application",
        },
      ],
    },
  ],
};

export default expressCheatsheet;
